// Procedural low-poly 3D building models. Each builder draws into a MeshBuilder at
// the origin, feet on y=0, +Y up, roughly facing -Z (the layer applies dir/seed yaw).
// One merged geometry per (type,colour) is cached. Owner colour tints roofs/banners.
// Pure geometry over domain enums. Owner: client-render3d/models.

import { BufferGeometry } from 'three'
import type { BuildingType } from '../../../shared/types'
import { townHallPhase, type TownHallPhase } from '../../../shared/townhall'
import { MeshBuilder } from './mesh'
import { MAT, rng3d } from './palette3d'
import { barrel, pennant, roof, windowDetail } from './details3d'
import { canvasRoof, townHallArchitecture } from './townhall3d'
import { farmArchitecture } from './farm3d'

interface Ctx {
  color: string
  seed: number
  rand: () => number
  /** Model variant (0 default). The townhall packs live state in here:
   *  bits 0-1 = store-fill bucket (0..3, the annex goods pile), bits 2+ =
   *  RACK_ITEMS bitmask of spare items mounted on the front weapon rack.
   *  Plant orders use -1 for queued planting, then 0..2 for the three growth days. */
  variant: number
  phase: TownHallPhase
}
type Builder = (mb: MeshBuilder, o: Ctx) => void

/** Item kinds the hall's weapon rack can mount, in display (and bitmask)
 *  order. The renderer builds the mask from the OWN faction's armory; foreign
 *  halls show bare pegs — their racks are private, like their stores. */
export const RACK_ITEMS = ['hammer', 'shovel', 'hoe', 'axe', 'pick', 'rod', 'spear', 'smithhammer', 'sword'] as const

// ---- individual models -------------------------------------------------------

const hut: Builder = (mb, { color }) => {
  mb.box(0, 8, 0, 26, 16, 22, MAT.plaster)
  mb.box(0, 6, -11.5, 6, 11, 2, MAT.woodDk) // door
  mb.gable(0, 16, 0, 30, 12, 26, MAT.thatch)
  mb.box(9, 22, 6, 4, 8, 4, MAT.stoneDk) // chimney
  mb.box(0, 27, 8, 1.4, 5, 1.4, color) // small owner pennant pole+flag
}

const house: Builder = (mb, { color, rand }) => {
  const y = 20 + rand() * 2
  mb.box(0, 2, 0, 30, 4, 26, MAT.stone)
  mb.box(0, (y + 4) / 2, 0, 27, y - 4, 23, MAT.plaster)
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) mb.box(sx * 13, (y + 4) / 2, sz * 11, 2, y - 3, 2, MAT.timber)
  for (const z of [-11.8, 11.8]) {
    mb.box(0, y - 1, z, 28, 1.6, 1.4, MAT.timber)
    mb.box(0, 7, z, 28, 1.4, 1.4, MAT.timber)
    for (const s of [-1, 1]) mb.beam([s * 12, 8, z], [s * 5, y - 2, z], 1.2, MAT.timber)
  }
  mb.box(0, 9, -12, 6, 12, 1.5, MAT.woodDk)
  for (const x of [-1.8, 0, 1.8]) mb.box(x, 9, -13, 1.4, 11, 0.4, MAT.wood)
  mb.box(2, 9, -13.4, 0.7, 0.7, 0.5, MAT.gold)
  mb.box(0, 1.2, -15, 9, 2.4, 5, MAT.stone)
  windowDetail(mb, -8.5, 14, -12, 3.3, 4)
  windowDetail(mb, 8.5, 14, -12, 3.3, 4)
  roof(mb, 0, y, 0, 33, 14, 28, MAT.thatch)
  mb.tri(-12.5, y + 0.8, -14.2, 0, y + 11.5, -14.2, 12.5, y + 0.8, -14.2, MAT.plaster)
  mb.box(0, y + 5.7, -14.5, 1.2, 11.2, 0.7, MAT.timber)
  mb.box(0, y + 1, -14.5, 26, 1.2, 0.7, MAT.timber)
  mb.box(9, 27, 6, 4.5, 16, 4.5, MAT.stone)
  mb.box(9, 35.5, 6, 6, 2, 6, MAT.stoneDk)
  mb.box(9, 36.6, 6, 3.5, 0.3, 3.5, MAT.coal)
  mb.box(0, 18, -12.5, 4, 2, 0.5, color)
  barrel(mb, 15, 0, 6, 2.6, 6)
}

const townhall: Builder = (mb, { color, variant, phase }) => {
  townHallArchitecture(mb, phase, color)
  // side workbench: where the hall's crafter stands whittling the wooden
  // tools (entities3d parks the working serf here — HALL_BENCH). East flank,
  // on bare ground in every phase, including the tiny starting campsite.
  mb.box(42, 8.5, 0, 18, 2.5, 9, MAT.woodPale) // benchtop
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) mb.box(42 + sx * 7.5, 4, sz * 3.2, 2, 8, 2, MAT.woodDk) // legs
  mb.box(38, 10.7, -1.5, 5, 2, 3.4, MAT.wood) // plank stack on the top
  mb.box(46.5, 10.2, 1.8, 3.2, 1.2, 1.8, MAT.woodDk) // mallet resting
  mb.cyl(33, 2.5, 9, 3.4, 5, 5, MAT.timber) // chopping stump beside
  // storage annex: the hall IS the village store — show it. A lean-to on the
  // west flank whose stacked goods GROW with the hub's actual fill (variant
  // 0..3 = the store bucket, keyed into the geometry by the renderer).
  mb.box(-42, 1.6, -2, 20, 3.2, 26, MAT.woodDk) // freestanding pallet, original goods floor y=4
  for (const x of [-49, -45, -41, -37]) mb.box(x, 3.6, -2, 3.1, .8, 25, MAT.wood)
  if (phase >= 2) {
    for (const x of [-50, -34]) for (const z of [-13, 9]) mb.box(x, 12, z, 1.7, 24, 1.7, MAT.timber)
    if (phase === 2) {
      canvasRoof(mb, -42, 22, -2, 21, 5, 29, MAT.cloth)
      mb.beam([-42, 27.5, -17.5], [-42, 27.5, 13.5], .85, MAT.woodPale)
    } else {
      mb.box(-42, 24, -2, 22, 2, 30, MAT.woodDk)
      for (let i = 0; i < 6; i++) mb.box(-50.8 + i * 3.5, 25.2, -2, 3.7, .75, 30, phase === 3 ? MAT.thatchDk : MAT.slate)
    }
  }
  const v = variant & 3
  if (v >= 1) {
    mb.box(-46, 7.5, -8, 7, 7, 7, MAT.woodDk) // crate
    mb.box(-38, 6.5, 5, 6, 5, 6, MAT.cloth) // grain sack
  }
  if (v >= 2) {
    mb.box(-38, 7, -8, 6, 6, 6, MAT.wood) // second crate
    mb.cyl(-47, 4, 2, 3.2, 8, 6, MAT.timber) // barrel
  }
  if (v >= 3) {
    mb.box(-46, 14, -8, 6, 6, 6, MAT.wood) // crate stacked on crate
    mb.box(-38, 10.5, 5, 5, 3.5, 5, MAT.cloth) // sacks piled higher
    mb.cyl(-41, 4, 2, 3.2, 8, 6, MAT.timber) // second barrel
  }
  // Freestanding weapon rack on the SOUTH side, facing the default camera: two
  // rails with the armory's SPARE gear mounted between them (bits 2+ of the
  // variant = RACK_ITEMS mask). Feet and braces make the original rails coherent
  // even before there is a hall wall. Item anchors never move with phase.
  const rack = variant >> 2
  for (const x of [6.5, 25.5]) {
    mb.box(x, 14, 24.1, 1.8, 28, 1.8, MAT.woodDk)
    mb.box(x, .8, 25, 3.1, 1.6, 8, MAT.timber)
    mb.beam([x, 12, 24.1], [x, 1.1, 29], 1, MAT.wood)
  }
  mb.box(16, 26, 24.6, 17, 1.4, 0.9, MAT.woodDk) // top rail
  mb.box(16, 17.5, 24.6, 17, 1.4, 0.9, MAT.woodDk) // bottom rail
  for (let i = 0; i < RACK_ITEMS.length; i++) {
    if (!(rack & (1 << i))) continue
    const at = rackSlotLocal(rack, RACK_ITEMS[i])
    if (at) drawRackItem(mb, RACK_ITEMS[i], at.x, at.y, at.z)
  }
}

// The rack's mounted-item geometry is shared with the craft-delivery flight
// (entities3d floats the finished piece from the bench to its slot, and only
// then does it appear ON the rack) — so both must agree exactly.
const RACK_Z = 25 // just proud of the south wall plane (z +24)
const RACK_MAX_SLOTS = 6

/** Where `item` hangs for a given rack mask, in townhall-local coordinates —
 *  or null when it isn't on the rack (absent, or past the slot cap). */
export function rackSlotLocal(mask: number, item: (typeof RACK_ITEMS)[number]): { x: number; y: number; z: number } | null {
  let slot = 0
  for (let i = 0; i < RACK_ITEMS.length && slot < RACK_MAX_SLOTS; i++) {
    if (!(mask & (1 << i))) continue
    if (RACK_ITEMS[i] === item) return { x: 9.6 + slot * 2.7, y: 21.5, z: RACK_Z }
    slot++
  }
  return null
}

/** One mounted item, drawn about (x, y≈handle centre, z). */
function drawRackItem(mb: MeshBuilder, item: (typeof RACK_ITEMS)[number], x: number, y: number, z: number): void {
  const hy = y - 0.5 // handle centre used by the original layout (y 21)
  if (item === 'sword') {
    mb.box(x, hy, z, 0.8, 6.5, 0.5, MAT.metal) // blade, point down
    mb.box(x, hy + 3.6, z, 2.2, 0.7, 0.7, MAT.metalDk) // crossguard
    return
  }
  if (item === 'spear') {
    mb.box(x, hy + 0.5, z, 0.6, 9, 0.6, MAT.woodDk) // full-height shaft
    mb.pyramid(x, hy + 4.8, z, 0.8, 1.8, 4, MAT.woodPale) // hardened point
    return
  }
  // hafted tools: a common handle with a head that tells them apart
  mb.box(x, hy, z, 0.7, 7, 0.7, item === 'rod' ? MAT.wood : MAT.woodDk)
  if (item === 'hammer') mb.box(x, hy + 3.6, z, 2.6, 1.4, 1.3, MAT.woodPale)
  else if (item === 'shovel') mb.box(x, hy + 3.8, z, 1.7, 2, 0.7, MAT.woodPale)
  else if (item === 'hoe') mb.box(x + 0.8, hy + 3.7, z, 1.8, 0.9, 0.9, MAT.woodPale)
  else if (item === 'axe') mb.box(x + 0.8, hy + 3.2, z, 1.5, 2.2, 0.8, MAT.woodPale)
  else if (item === 'pick') mb.box(x, hy + 3.7, z, 3.2, 0.9, 0.9, MAT.woodPale)
  else if (item === 'smithhammer') mb.box(x, hy + 3.4, z, 2.2, 1.9, 1.6, MAT.metal)
  // rod: the bare pale pole IS the item
}

const rackItemCache = new Map<string, BufferGeometry>()
/** A single rack item's geometry at the ORIGIN — the delivery flight's mesh. */
export function rackItemGeometry(item: (typeof RACK_ITEMS)[number]): BufferGeometry {
  let g = rackItemCache.get(item)
  if (g) return g
  const mb = new MeshBuilder()
  drawRackItem(mb, item, 0, 0.5, 0) // handle centre at y 0
  g = mb.geometry()
  rackItemCache.set(item, g)
  return g
}

const storehouse: Builder = (mb, { color }) => {
  mb.box(0, 3, 0, 40, 6, 34, MAT.dirt) // raised platform
  mb.box(0, 12, 0, 34, 14, 28, MAT.wood)
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) mb.box(sx * 17, 12, sz * 14, 3, 18, 3, MAT.timber)
  roof(mb, 0, 21, 0, 40, 13, 34, MAT.slate)
  for (const x of [-8, 8]) {
    mb.box(x, 12, -14.5, 14, 14, 1, MAT.woodDk)
    for (let j = -2; j <= 2; j++) mb.box(x + j * 2.5, 12, -15.2, 2, 13, 0.5, MAT.wood)
    mb.beam([x - 6, 6, -15.7], [x + 6, 18, -15.7], 1.2, MAT.woodPale)
  }
  for (const x of [-19, 19]) barrel(mb, x, 3, 6)
  pennant(mb, 0, 35, 0, color)
  // crates & sacks out front
  mb.box(-12, 8, -19, 8, 8, 8, MAT.woodDk)
  mb.box(-4, 7, -20, 6, 6, 6, MAT.wood)
  mb.box(9, 6, -19, 7, 6, 7, MAT.cloth)
  mb.box(0, 26, 10, 1.4, 6, 1.4, color)
}

const tower: Builder = (mb, { color }) => {
  mb.cyl(0, 0, 0, 15, 5, 8, MAT.stoneDk)
  mb.cyl(0, 0, 0, 12, 44, 6, MAT.stone)
  mb.cyl(0, 44, 0, 14, 5, 6, MAT.stoneDk) // battlement ring
  // crenellations
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2
    mb.box(Math.cos(a) * 12.5, 51, Math.sin(a) * 12.5, 4, 5, 4, MAT.stoneDk)
  }
  mb.pyramid(0, 49, 0, 12, 18, 6, color)
  mb.pyramid(0, 55, 0, 9, 14, 6, color)
  for (const y of [13, 27, 40]) mb.cyl(0, y, 0, 12.5, 1.5, 6, MAT.stoneDk)
  mb.box(0, 31, -10.5, 2, 7, 0.5, MAT.coal)
  mb.box(0, 10, -10.6, 5, 12, 0.5, MAT.woodDk)
  mb.box(0, 60, 0, 1.4, 10, 1.4, MAT.woodDk)
  mb.box(4, 63, 0, 7, 5, 0.6, color)
}

const palisade: Builder = (mb) => {
  for (let i = -1; i <= 1; i++) {
    const x = i * 12
    mb.cyl(x, 0, 0, 4.2, 22 + (i === 0 ? 3 : 0), 5, MAT.timber)
    mb.pyramid(x, 22 + (i === 0 ? 3 : 0), 0, 4.4, 5, 5, MAT.woodDk) // pointed tip
  }
  mb.box(0, 16, 0, 40, 3, 2.4, MAT.woodDk) // cross rail
}

// Terraform markers are small "work here" spots, NOT structures — a floating status
// badge (hud3d.orderIcons) is the real indicator, so keep the ground piece low.
const dig: Builder = (mb) => {
  mb.box(-2, 1.2, 1, 8, 2.4, 8, MAT.dirt, 0.2) // a small patch of turned earth
  mb.box(2, 2.6, -2, 4, 2.2, 4, MAT.soil, -0.3) // loose spoil
  mb.cyl(4, 0, -4, 0.5, 11, 4, MAT.woodDk) // shovel stood upright
  mb.box(4, 11.5, -4, 2.2, 2.8, 0.8, MAT.metal)
}

const fill: Builder = (mb) => {
  mb.box(-1, 1.6, 0, 9, 3.2, 9, MAT.dirt, 0.15) // a small mound of dirt to tamp in
  mb.box(1, 3.8, -1, 5, 2.2, 5, MAT.soil, -0.2)
  mb.cyl(5, 0, -4, 0.5, 11, 4, MAT.woodDk)
  mb.box(5, 11.5, -4, 2.2, 2.8, 0.8, MAT.metal)
}

const fillsand: Builder = (mb) => {
  mb.box(-1, 1.6, 0, 9, 3.2, 9, '#d8c48e', 0.15) // a small mound of pale sand
  mb.box(1, 3.8, -1, 5, 2.2, 5, '#c9b489', -0.2)
  mb.cyl(5, 0, -4, 0.5, 11, 4, MAT.woodDk)
  mb.box(5, 11.5, -4, 2.2, 2.8, 0.8, MAT.metal)
}

const farm: Builder = (mb, { color, seed }) => farmArchitecture(mb, color, seed)

// A "marked for felling" order — a low woodcutter's spot at the BASE of the tree
// (the tree keeps standing until the fell finishes): a cut log with an axe struck
// into it and a couple of fresh chips. Deliberately small so it never reads as a pit.
const chop: Builder = (mb) => {
  mb.box(-5, 1.6, 3, 10, 2.6, 2.6, MAT.timber, 0.15) // a felled log lying on its side
  mb.box(2, 1.2, -2, 5, 2.2, 3, MAT.woodDk, 0.5) // a chopping billet
  mb.box(3.5, 5.5, -2, 1.1, 8, 1.1, MAT.timber, -0.45) // axe handle, struck into the billet
  mb.box(3.0, 9.5, -2, 3.4, 2.6, 1, MAT.metal, -0.45) // axe head
  mb.box(-1, 0.7, 6, 2.4, 1.2, 1.6, MAT.wood, 0.3) // a chip
  mb.box(6, 0.7, 3, 2, 1, 1.4, MAT.woodDk, -0.4) // another chip
}

const quarry: Builder = (mb) => {
  mb.box(0, 2, 0, 42, 4, 38, MAT.dirt) // dug scar
  mb.box(-10, 6, -6, 11, 8, 11, MAT.stone) // cut blocks
  mb.box(-10, 15, -6, 8, 7, 8, MAT.stoneDk)
  mb.box(7, 5, 7, 9, 7, 9, MAT.stone)
  mb.box(13, 4, -8, 7, 5, 7, MAT.stoneDk)
  mb.box(1, 3, 13, 6, 3, 5, MAT.stone) // rubble
  mb.box(-16, 9, 11, 1, 15, 1, MAT.woodDk) // leaning pick
  mb.box(-16, 16, 11, 10, 1.6, 1.6, MAT.metalDk)
  mb.cyl(15, 0, 11, 1.6, 20, 4, MAT.timber) // hoist post
  mb.box(15, 20, 6, 1.4, 1.4, 12, MAT.timber) // jib
}

const mine: Builder = (mb) => {
  mb.gem(-7, 11, 7, 37, 28, 33, MAT.stoneDk, 7)
  mb.gem(11, 10, 8, 26, 23, 29, MAT.stone, 5)
  for (const x of [-5.5, 5.5]) mb.box(x, 8, -13, 2.5, 15, 4, MAT.timber)
  mb.box(0, 7, -13, 8, 12, 1, MAT.coal) // recessed adit opening
  mb.box(0, 16, -13, 15, 3, 4, MAT.woodDk) // lintel
  mb.box(0, 4, -22, 9, 6, 7, MAT.woodDk) // ore cart
  mb.box(0, 8, -22, 7.5, 3, 5.5, MAT.coal)
  for (const x of [-5, 5]) {
    mb.box(x, 0.7, -21, 0.7, 1.4, 20, MAT.metalDk)
    for (const z of [-24, -19]) mb.gem(x, 2, z, 2, 4, 4, MAT.metalDk, 6)
  }
  for (const z of [-29, -24, -19, -14]) mb.box(0, 0.5, z, 13, 1, 1.5, MAT.timber)
}

const barracks: Builder = (mb, { color }) => {
  mb.box(0, 4, 0, 46, 8, 30, MAT.stone)
  mb.box(0, 16, 0, 40, 18, 24, MAT.timber)
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) mb.box(sx * 20, 16, sz * 12, 3, 18, 3, MAT.woodDk)
  roof(mb, 0, 25, 0, 47, 15, 30, MAT.slate)
  for (const x of [-13, 13]) windowDetail(mb, x, 18, -12.8, 4, 5)
  mb.box(0, 13, -15, 9, 16, 2, MAT.coal) // doorway
  mb.box(-16, 30, -15, 6, 9, 0.6, color) // banners
  mb.box(16, 30, -15, 6, 9, 0.6, color)
  mb.box(-14, 6, -20, 13, 1.4, 2, MAT.woodDk) // weapon rack
  for (let i = -2; i <= 2; i++) { mb.box(-14 + i * 2.7, 11, -20, 0.6, 11, 0.6, MAT.woodDk); mb.pyramid(-14 + i * 2.7, 16.5, -20, 0.9, 2.5, 4, MAT.metal) }
  // The legacy barracks type is now the smithy: a forge and anvil identify it.
  mb.box(16, 8, 5, 12, 16, 12, MAT.stoneDk)
  mb.box(16, 22, 5, 7, 28, 7, MAT.stone)
  mb.box(16, 37, 5, 9, 2, 9, MAT.stoneDk)
  mb.box(16, 7, -1.3, 7, 7, 0.6, MAT.coal)
  mb.box(16, 5, -1.7, 5, 2, 0.5, '#dd793d')
  mb.cyl(9, 0, -21, 4, 5, 6, MAT.timber)
  mb.box(9, 7, -21, 4, 4, 4, MAT.metalDk)
  mb.box(9, 9.5, -21, 9, 2, 4.5, MAT.metal)
}

const workbench: Builder = (mb) => {
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) mb.cyl(sx * 13, 0, sz * 11, 1.6, 16, 4, MAT.timber)
  mb.box(0, 18, 0, 32, 2, 28, MAT.woodDk)
  mb.gable(0, 19, 0, 32, 6, 28, MAT.thatchDk)
  mb.box(0, 7, -3, 22, 3, 9, MAT.wood) // bench top
  mb.box(0, 3.5, -3, 20, 7, 2, MAT.woodDk) // bench legs
  mb.box(9, 9, 5, 11, 1.5, 7, MAT.wood) // planks
  mb.box(-9, 10, 5, 3, 5, 2, MAT.metal) // mallet head
  mb.box(-9, 6, 5, 1, 6, 1, MAT.woodDk) // mallet handle
}

const dummy: Builder = (mb) => {
  mb.cyl(0, 0, 0, 2, 16, 6, MAT.woodDk) // post
  mb.box(0, 18, 0, 21, 2.6, 2.6, MAT.timber) // cross-arms
  mb.box(0, 20.5, 0, 8, 10, 6, MAT.thatch) // straw body
  mb.box(0, 28, 0, 5, 5, 5, MAT.thatchDk) // head
  mb.box(0, 24, -3.4, 11, 8, 1, MAT.cloth) // burlap target
}

const pier: Builder = (mb) => {
  for (let i = 0; i < 4; i++) {
    const z = -6 - i * 10
    mb.box(0, 6, z, 16, 2, 9, MAT.wood)
    for (const sx of [-1, 1]) mb.cyl(sx * 6, -8, z, 1.4, 14, 4, MAT.woodDk)
  }
  mb.cyl(6, 7, -36, 1.8, 9, 5, MAT.woodDk) // mooring post
  mb.box(-5, 8, -30, 6, 5, 6, MAT.woodDk) // a crate on the dock
}

const ramp: Builder = (mb) => {
  for (let i = 0; i < 6; i++) { const z = 14 - i * 5.6, h = 2 + i * 1.7; mb.box(0, h / 2, z, 18, h, 6.4, MAT.wood) }
  for (const sx of [-1, 1]) mb.box(sx * 8.4, 6, -1, 1.5, 12, 34, MAT.woodDk) // side rails
}

const camp: Builder = (mb) => {
  mb.pyramid(-9, 0, -4, 9, 15, 4, '#6b5a3a', Math.PI / 4) // ragged tents
  mb.pyramid(10, 0, 6, 8, 13, 4, '#5a4b31', Math.PI / 4)
  for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2; mb.box(Math.cos(a) * 4, 1.5, 3 + Math.sin(a) * 4, 4, 2, 1.4, MAT.woodDk, a) }
  mb.pyramid(0, 2, 3, 3, 6, 4, '#c85a28') // campfire flame
  mb.cyl(15, 0, -9, 1.7, 19, 5, MAT.woodDk) // spiked totem
  mb.pyramid(15, 19, -9, 1.9, 4, 4, MAT.metalDk)
  mb.box(15, 15, -9, 3.5, 3.5, 3.5, '#c9c2b0') // pale skull
}

/** A mulched planting spot becomes a shoot, a branching sapling, then a young
 *  broadleaf tree. The mature tree belongs to the terrain, not this model. */
const plant: Builder = (mb, { variant, rand }) => {
  mb.cyl(0, 0.65, 0, 7.5, 0.8, 7, '#795a3a')
  for (let i = 0; i < 5; i++) {
    const a = i * Math.PI * 2 / 5
    mb.box(Math.cos(a) * 5.3, 1.6, Math.sin(a) * 5.3, 2.1, 0.55, 0.9, '#b0935d', a)
  }
  if (variant < 0) return
  const age = Math.max(0, Math.min(2, variant))
  const height = [12, 22, 32][age]
  const stem = age === 0 ? '#809647' : '#7d5a35'
  const lean = rand() * 1.5 - 0.75
  mb.beam([0, 1, 0], [lean, height, 0], [0.9, 1.3, 1.8][age], stem)
  const count = 3 + age * 2
  for (let i = 0; i < count; i++) {
    const a = i * 2.4 + 0.6
    const y = height * (0.38 + i / count * 0.53)
    const reach = [3.1, 4.4, 5.8][age] * (1 - i / count * 0.38)
    const x = Math.cos(a) * reach + lean, z = Math.sin(a) * reach
    mb.beam([lean * y / height, y - 2, 0], [x, y, z], age === 0 ? 0.55 : 0.8, stem)
    const width = [5.5, 8, 10][age] * (0.82 + rand() * 0.25)
    mb.gem(x, y + 0.6, z, width, age === 0 ? 2.3 : width * 0.7, width * 0.7,
      ['#92c66a', '#5a9f52', '#bbd77a'][i % 3], 5)
  }
  if (age > 0) mb.gem(lean, height, 0, 6 + age * 2, 6 + age * 3, 6 + age, '#a8d372', 5)
}

// ---- registry ----------------------------------------------------------------

const REG: Partial<Record<BuildingType, Builder>> = {
  house, townhall, storehouse, tower, palisade, farm, dig, fill, fillsand,
  chop, plant, quarry, mine, barracks, workbench, dummy, pier, ramp, camp,
}

const cache = new Map<string, BufferGeometry>()

/** Cached merged geometry for a building. Falls back to a generic hut. */
export function buildingGeometry(type: BuildingType, color: string, seed: number, variant = 0, phase: TownHallPhase = 1): BufferGeometry {
  const modelPhase = townHallPhase(phase)
  const key = `${type}:${color}:${seed & 3}:${variant}${type === 'townhall' ? `:phase${modelPhase}` : ''}`
  let g = cache.get(key)
  if (g) return g
  const mb = new MeshBuilder()
  ;(REG[type] ?? hut)(mb, { color, seed, rand: rng3d(seed || 1), variant, phase: modelPhase })
  g = mb.geometry()
  cache.set(key, g)
  return g
}

/** Whether we have a bespoke model (vs the hut fallback) — handy for logging. */
export function hasBuildingModel(type: BuildingType): boolean {
  return type in REG
}

// ---- construction-site props -------------------------------------------------------

/** Survey stakes marking a freshly-placed, still-empty site. */
export function stakesGeometry(): BufferGeometry {
  const key = 'site:stakes'
  let g = cache.get(key)
  if (g) return g
  const mb = new MeshBuilder()
  for (let i = 0; i < 4; i++) {
    const a = (Math.PI / 180) * (45 + i * 90)
    const x = Math.cos(a) * 13, z = Math.sin(a) * 13
    mb.box(x, 2.6, z, 1.2, 5.2, 1.2, MAT.woodDk)
    mb.box(x, 4.6, z, 2.2, 0.8, 2.2, MAT.cloth) // pale marker cap
  }
  g = mb.geometry()
  cache.set(key, g)
  return g
}

/** A pile of delivered building materials at a site — scaled by delivery fraction. */
export function sitePileGeometry(res: string): BufferGeometry {
  const key = `site:pile:${res}`
  let g = cache.get(key)
  if (g) return g
  const mb = new MeshBuilder()
  if (res === 'wood') {
    // stacked logs, pale cut ends
    mb.box(-2.6, 1.4, 0, 3.2, 2.8, 9, MAT.wood)
    mb.box(0.8, 1.4, 0, 3.2, 2.8, 9, MAT.timber)
    mb.box(4, 1.4, 0.4, 3, 2.6, 8, MAT.woodDk)
    mb.box(-1, 4.1, 0, 3.2, 2.6, 8.4, MAT.woodDk)
    mb.box(2.4, 4, 0.2, 3, 2.4, 7.6, MAT.wood)
    mb.box(0.8, 6.4, 0, 3, 2.4, 7.2, MAT.timber)
  } else if (res === 'stone') {
    // rough quarried blocks
    mb.box(-2.4, 1.6, -1.4, 4.6, 3.2, 4.4, MAT.stone, 0.2)
    mb.box(2.6, 1.5, 1, 4.2, 3, 4, MAT.stoneDk, -0.3)
    mb.box(0.4, 1.4, 3.4, 3.6, 2.8, 3.4, MAT.slate, 0.5)
    mb.box(0, 4.4, 0, 4, 2.8, 3.8, MAT.stone, -0.2)
  } else {
    mb.box(0, 2.2, 0, 6, 4.4, 5, MAT.cloth) // generic crated goods
  }
  g = mb.geometry()
  cache.set(key, g)
  return g
}
